import { useState } from "react";
import { Check, Package, Puzzle, X } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Collapsible, CollapsibleContent, CollapsibleTrigger, EmptyState, Input, MarkdownBody, NativeSelect, Skeleton, notifySaved } from "@ncp/ui";
import { useCapabilityRequests, usePageActions, usePagePackages, type ApprovedPackage, type CapabilityRequest } from "./api";

const packageTone = (s: string) => (s === "Approved" ? "success" : s === "Rejected" ? "danger" : "warning") as "success" | "danger" | "warning";
const requestTone = (s: string) => (s === "Delivered" ? "success" : s === "Accepted" ? "info" : s === "Declined" ? "outline" : "warning") as "success" | "info" | "outline" | "warning";

/** What authors (and the AI author) asked the platform team for: npm packages for the air-gapped store, and capabilities the platform lacks. */
export function RequestsPanel() {
  const { t } = useTranslation();
  const packages = usePagePackages();
  const requests = useCapabilityRequests();
  const [pkgFilter, setPkgFilter] = useState("Requested");
  const [reqFilter, setReqFilter] = useState("Open");
  const pkgs = (packages.data?.packages ?? []).filter((p) => !pkgFilter || p.status === pkgFilter);
  const reqs = (requests.data ?? []).filter((r) => !reqFilter || r.status === reqFilter);

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3">
          <div><CardTitle className="flex items-center gap-2"><Package className="size-4 text-aegold-700" /> {t("admin:pageStudio.requests.packages")}</CardTitle><CardDescription>{t("admin:pageStudio.requests.packagesHint")}</CardDescription></div>
          <NativeSelect value={pkgFilter} onChange={(e) => setPkgFilter(e.target.value)} className="h-8 w-36">
            <option value="">{t("admin:common.all")}</option>
            {["Requested", "Approved", "Rejected"].map((s) => <option key={s} value={s}>{t(`admin:pageStudio.requests.packageStatus.${s}`)}</option>)}
          </NativeSelect>
        </CardHeader>
        <CardContent>
          {packages.isLoading ? <Skeleton className="h-40" /> : pkgs.length === 0 ? <EmptyState title={t("admin:pageStudio.requests.noPackages")} className="py-8" /> : (
            <ul className="divide-y divide-sand-100">{pkgs.map((p) => <PackageRow key={p.id} p={p} />)}</ul>
          )}
          {packages.data && !packages.data.builder.available ? <p className="mt-2 text-xs text-warning-700">{t("admin:pageStudio.requests.builderDown")}</p> : null}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3">
          <div><CardTitle className="flex items-center gap-2"><Puzzle className="size-4 text-aegold-700" /> {t("admin:pageStudio.requests.capabilities")}</CardTitle><CardDescription>{t("admin:pageStudio.requests.capabilitiesHint")}</CardDescription></div>
          <NativeSelect value={reqFilter} onChange={(e) => setReqFilter(e.target.value)} className="h-8 w-36">
            <option value="">{t("admin:common.all")}</option>
            {["Open", "Accepted", "Declined", "Delivered"].map((s) => <option key={s} value={s}>{t(`admin:pageStudio.requests.requestStatus.${s}`)}</option>)}
          </NativeSelect>
        </CardHeader>
        <CardContent>
          {requests.isLoading ? <Skeleton className="h-40" /> : reqs.length === 0 ? <EmptyState title={t("admin:pageStudio.requests.noRequests")} className="py-8" /> : (
            <ul className="divide-y divide-sand-100">{reqs.map((r) => <RequestRow key={r.id} r={r} />)}</ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PackageRow({ p }: { p: ApprovedPackage }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const actions = usePageActions();
  const [note, setNote] = useState("");
  const [licence, setLicence] = useState(p.licence ?? "");
  const decide = async (approve: boolean) => {
    try { notifySaved(await actions.decidePackage.mutateAsync({ id: p.id, approve, note, licence }), approve ? t("admin:pageStudio.requests.approved", { name: p.name }) : t("admin:pageStudio.requests.rejected", { name: p.name })); }
    catch { /* useSave reported it */ }
  };
  return (
    <li className="py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <code className="font-mono text-sm font-medium" dir="ltr">{p.name}@{p.version}</code>
        <Badge size="sm" variant={packageTone(p.status)}>{t(`admin:pageStudio.requests.packageStatus.${p.status}`, { defaultValue: p.status })}</Badge>
        {p.licence ? <Badge size="sm" variant="outline">{p.licence}</Badge> : null}
        {p.vendor ? <Badge size="sm" variant="camel">{t("admin:pageStudio.requests.vendor")}</Badge> : null}
        <span className="ms-auto text-xs text-muted-foreground">{[p.requestedByName, p.requestedForPageKey].filter(Boolean).join(" · ")}</span>
      </div>
      {p.purpose ? <p className="mt-1 text-xs text-muted-foreground">{p.purpose}</p> : null}
      {p.status === "Requested" && can("pages.packages") ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input value={licence} onChange={(e) => setLicence(e.target.value)} placeholder={t("admin:pageStudio.requests.licence")} className="h-8 w-32" dir="ltr" />
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("admin:pageStudio.requests.note")} className="h-8 min-w-40 flex-1" />
          <Button size="sm" variant="success" loading={actions.decidePackage.isPending} onClick={() => void decide(true)}><Check /> {t("admin:pageStudio.requests.approve")}</Button>
          <Button size="sm" variant="outline" onClick={() => void decide(false)}><X /> {t("admin:pageStudio.requests.reject")}</Button>
        </div>
      ) : p.decidedAt ? <p className="mt-1 text-[11px] text-muted-foreground">{t("admin:pageStudio.requests.decided", { name: p.decidedByName ?? "—", when: formatRelative(p.decidedAt, locale) })}{p.decisionNote ? ` · ${p.decisionNote}` : ""}</p> : null}
    </li>
  );
}

function RequestRow({ r }: { r: CapabilityRequest }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const actions = usePageActions();
  const [resolution, setResolution] = useState(r.resolution ?? "");
  const resolve = async (status: string) => {
    try { notifySaved(await actions.resolveCapability.mutateAsync({ id: r.id, status, resolution }), t("admin:pageStudio.requests.resolved", { status: t(`admin:pageStudio.requests.requestStatus.${status}`) })); }
    catch { /* useSave reported it */ }
  };
  return (
    <li className="py-2.5">
      <Collapsible>
        <div className="flex flex-wrap items-center gap-2">
          <CollapsibleTrigger className="min-w-0 flex-1 truncate text-start text-sm font-medium hover:text-aegold-800">{r.title}</CollapsibleTrigger>
          <Badge size="sm" variant={requestTone(r.status)}>{t(`admin:pageStudio.requests.requestStatus.${r.status}`, { defaultValue: r.status })}</Badge>
        </div>
        <p className="text-xs text-muted-foreground">{[r.requestedByName, r.pageKey, r.spaceKey, formatRelative(r.createdAt, locale)].filter(Boolean).join(" · ")}</p>
        <CollapsibleContent>
          <div className="mt-2 space-y-2 rounded-lg bg-sand-50 p-3 text-sm">
            <MarkdownBody>{r.spec}</MarkdownBody>
            {r.draftSchemaJson ? <pre className="max-h-56 overflow-auto rounded-md bg-white p-2 font-mono text-[11px]" dir="ltr">{(() => { try { return JSON.stringify(JSON.parse(r.draftSchemaJson), null, 2); } catch { return r.draftSchemaJson; } })()}</pre> : null}
            {r.resolution ? <p className="text-xs"><span className="font-medium">{t("admin:pageStudio.requests.resolution")}:</span> {r.resolution}</p> : null}
            {can("pages.packages") ? (
              <div className="flex flex-wrap items-center gap-2">
                <Input value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder={t("admin:pageStudio.requests.resolutionPlaceholder")} className="h-8 min-w-48 flex-1" />
                {["Accepted", "Delivered", "Declined"].filter((s) => s !== r.status).map((s) => (
                  <Button key={s} size="sm" variant={s === "Declined" ? "outline" : "gold"} loading={actions.resolveCapability.isPending} onClick={() => void resolve(s)}>{t(`admin:pageStudio.requests.mark.${s}`)}</Button>
                ))}
              </div>
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
}
